import React, { useState } from 'react';
import Page from '../../../components/Page';
import { Icon, Breadcrumb, Tab, Button, Modal, Header, Message, Segment } from 'semantic-ui-react';
import { Link, Prompt, Route, matchPath, NavLink } from 'react-router-dom';
import useFetch from 'use-http';
import { Formik } from 'formik';
import _ from 'lodash';
import MyMenu from '../menu';
import AccountProgress from '../../../components/AccountProgress';
import { useTranslation } from 'react-i18next';
import { AccountList, toAccountList, cleanAccountList } from './marketAccounts';
const fetch_config = { responseType: 'json', cachePolicy: 'no-cache' };

const ACCOUNT_DEFAULTS = { active: false, name: '', appKey: '', appSecret: '' };
const REQUIRED_FIELDS = ['appKey', 'appSecret'];

function GeneralConfigs() {
	const { t } = useTranslation();
	const fields = [
		{ name: 'name', title: t('storeName'), placeholder: t('storeName') },
		{ name: 'appKey', title: 'API Key', placeholder: 'API Key' },
		{ name: 'appSecret', title: 'API Secret', placeholder: 'API Secret' },
	];
	return (
		<Tab.Pane>
			<Segment>
				<p>{t('n11QuestionDesc')}</p>
			</Segment>
			<AccountList name="accounts" fields={fields} defaults={ACCOUNT_DEFAULTS} />
		</Tab.Pane>
	);
}

function Configs() {
	const { t } = useTranslation();
	const panes = [
		{
			menuItem: {
				as: NavLink,
				content: t('general'),
				to: '/settings/integrations/n11',
				exact: true,
				key: 'n11',
			},
			render: () => <Route path="/settings/integrations/n11" exact component={GeneralConfigs} />,
		},
	];
	// Required to get a correct tab opened by default
	const defaultActiveIndex = panes.findIndex((pane) => {
		return !!matchPath(window.location.pathname, {
			path: pane.menuItem.to,
			exact: true,
		});
	});
	return (
		<Page.Wrap>
			<Tab menu={{ secondary: true, pointing: true }} defaultActiveIndex={defaultActiveIndex} panes={panes} />
		</Page.Wrap>
	);
}

export default function ClientSettings() {
	const { t } = useTranslation();
	const { loading, response } = useFetch('/api/dahi/message/getLiveChat', fetch_config, []);
	const result = response && response.data && response.data.result;
	const [isShowing, setIsShowing] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [message, setMessage] = useState('');
	const [messageType, setMessageType] = useState('');
	const [showMessage, setShowMessage] = useState(true);
	const initialValues = React.useMemo(() => {
		if (!result) return;
		const { n11Options } = result || {};
		// n11Options eski kayıtlarda tek obje, yeni kayıtlarda mağaza dizisi
		return { accounts: toAccountList(n11Options, ACCOUNT_DEFAULTS) };
	}, [result]);
	if (loading)
		return (
			<Page
				header={
					<Breadcrumb size="big">
						<Breadcrumb.Section as={Link} to={`/settings`}>
							<Icon name="setting" />
							{t('settings')}
						</Breadcrumb.Section>
						<Breadcrumb.Divider icon="right chevron" />

						<Breadcrumb.Section as={Link} to={`/settings/integrations`}>
							{t('integrations')}
						</Breadcrumb.Section>
						<Breadcrumb.Divider icon={{ name: 'asterisk', loading: true }} />
					</Breadcrumb>
				}
				flex
			>
				<MyMenu activeItem="n11">
					<Page.Loader />
				</MyMenu>
			</Page>
		);
	function openModal() {
		setIsShowing(true);
	}
	function closeModal() {
		setIsShowing(false);
	}
	function removeN11() {
		setDeleting(true);
		fetch('/api/dahi/channel/save', {
			method: 'POST',
			body: JSON.stringify({
				channel: 'live',
				data: { set: { n11Options: [], staticOptions: { n11_questions: false } } },
			}),
			headers: {
				'Content-Type': 'application/json',
			},
		})
			.then((response) => {
				return response.json();
			})
			.then((json) => {
				if (json.success) {
					setShowMessage(false);
					setIsShowing(false);
					setMessageType('positive');
					setMessage('Your N11 account deleted.');
					setDeleting(false);
					window.location.reload();
				} else {
					setShowMessage(false);
					setIsShowing(false);
					setMessageType('negative');
					setMessage('Error');
					setDeleting(false);
				}
			});
	}
	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values, actions) => {
				// her zaman dizi olarak kaydediyoruz, eski tek obje kayıtları da böylece diziye dönüyor
				let accounts = cleanAccountList(values.accounts, REQUIRED_FIELDS);
				fetch('/api/dahi/channel/save', {
					method: 'POST',
					body: JSON.stringify({
						channel: 'live',
						// livechat konuşma listesinde soru filtresi için staticOptions'a da yaz
						data: { set: { n11Options: accounts, staticOptions: { n11_questions: accounts.length > 0 } } },
					}),
					headers: {
						'Content-Type': 'application/json',
					},
				})
					.then((response) => {
						return response.json();
					})
					.then((json) => {
						if (json.success) {
							setShowMessage(false);
							setMessageType('positive');
							setMessage('Your N11 account connected.');
							actions.resetForm({ accounts: toAccountList(accounts, ACCOUNT_DEFAULTS) });
						} else {
							setShowMessage(false);
							setMessageType('negative');
							setMessage('Error');
							if (/"success":false/.test(json.message)) {
								setMessage('Error: Failed to connect to N11.');
							}
							actions.resetForm(values);
						}
						actions.setSubmitting(false);
					})
					.catch((e) => {
						console.error(e);
						setShowMessage(false);
						setMessageType('negative');
						setMessage('Error');
						actions.setSubmitting(false);
					});
			}}
		>
			{(props) => {
				return (
					<Page
						header={
							<Breadcrumb size="big">
								<Breadcrumb.Section as={Link} to={`/settings`}>
									<Icon name="setting" />
									{t('settings')}
								</Breadcrumb.Section>
								<Breadcrumb.Divider icon="right chevron" />

								<Breadcrumb.Section as={Link} to={`/settings/integrations`}>
									{t('integrations')}
								</Breadcrumb.Section>
								<Breadcrumb.Divider icon="right chevron" />
								<Breadcrumb.Section>N11 {t('settings')}</Breadcrumb.Section>
							</Breadcrumb>
						}
						extra={
							<div className="ml-auto d-flex">
								<AccountProgress></AccountProgress>
								<Button
									disabled={!_.some(props.values.accounts, 'active')}
									style={{ marginLeft: '10px' }}
									negative
									type="button"
									onClick={() => openModal()}
								>
									{t('remove')}
								</Button>
								<Button className="ml-auto" loading={props.isSubmitting} primary type="submit" onClick={props.submitForm}>
									{t('saveChanges')}
								</Button>
							</div>
						}
						flex
					>
						<MyMenu activeItem="n11">
							<form onSubmit={props.handleSubmit} autoComplete="off">
								<Prompt
									when={props.dirty}
									message={(location) =>
										location.pathname.startsWith('/settings/integrations/website-chat') ? true : t('questionWithoutSaving')
									}
								/>

								<Configs />
								<div style={{ textAlign: 'center' }}>
									<Message compact hidden={showMessage} positive={messageType === 'positive'} negative={messageType === 'negative'}>
										{message}
									</Message>
								</div>
							</form>
						</MyMenu>
						{/*delete modal*/}
						<Modal open={isShowing} onClose={() => closeModal()} style={{ width: '500px' }}>
							<Header icon="trash" content={t('removeTsoftConnection', { title: 'N11' })} />
							<Modal.Content>
								<p>{t('n11Remove')}</p>
							</Modal.Content>
							<Modal.Actions>
								<Button color="red" onClick={() => closeModal()}>
									<Icon name="remove" /> {t('no')}
								</Button>
								<Button color="green" loading={deleting} onClick={() => removeN11()}>
									<Icon name="checkmark" /> {t('yes')}
								</Button>
							</Modal.Actions>
						</Modal>
					</Page>
				);
			}}
		</Formik>
	);
}
